/** Drives @state chatWaitNow while a chat run is in progress (live wait counter). */

import { clearLiveState } from "./chat-live-state.ts";

export const CHAT_WAIT_TICK_MS = 200;

export type ChatWaitTickerHost = {
  chatRunId: string | null;
  chatSending: boolean;
  chatWaitNow: number;
  chatWaitTicker: number | null;
};

/**
 * Optional stream-state on the host. Cleanup paths that null `chatRunId`
 * directly (without going through a Lit-reactive render) must also clear
 * the in-flight stream text — `shouldShowLiveRunIndicator` returns true
 * whenever `streamText.length > 0`, so leftover delta text keeps the
 * activity bubble rendered with stale elapsed time even after the ticker
 * interval is stopped.
 */
export type ChatWaitStreamState = {
  chatStream: string | null;
  chatReasoningStream: string | null;
  chatStreamStartedAt: number | null;
  chatLiveRunHint?: string | null;
  chatActiveFileToolPath?: string | null;
  chatMainRun?: unknown;
};

export type ChatWaitFullHost = ChatWaitTickerHost &
  ChatWaitStreamState & {
    sessionKey?: string;
  };

export function isChatWaitTickerBusy(
  host: Pick<ChatWaitTickerHost, "chatRunId" | "chatSending">,
): boolean {
  return host.chatSending || Boolean(host.chatRunId);
}

export function syncChatWaitTicker(host: ChatWaitTickerHost): void {
  if (!isChatWaitTickerBusy(host)) {
    stopChatWaitTicker(host);
    return;
  }
  if (host.chatWaitTicker != null) {
    return;
  }
  host.chatWaitNow = Date.now();
  host.chatWaitTicker = window.setInterval(() => {
    host.chatWaitNow = Date.now();
  }, CHAT_WAIT_TICK_MS);
}

export function stopChatWaitTicker(host: ChatWaitTickerHost): void {
  if (host.chatWaitTicker != null) {
    clearInterval(host.chatWaitTicker);
    host.chatWaitTicker = null;
  }
}

/**
 * Tear down the live-run indicator end-to-end: stop the ticker interval
 * AND null every piece of stream state that `shouldShowLiveRunIndicator`
 * and `renderChat` derive from. Use this from any cleanup path that
 * previously set `chatRunId = null` directly (Lit `@state()` writes do
 * NOT guarantee that the next `updated()` callback runs immediately, so
 * the `setInterval` can keep firing for several seconds and the bubble
 * keeps showing stale elapsed time until the next render lands).
 *
 * Idempotent — safe to call when the run was never started, when state
 * is already nulled, and from `onHello` reconnect paths.
 *
 * When the host carries a `sessionKey` field and a live-state cache
 * (see `chat-live-state.ts`), the matching snapshot is also dropped —
 * the run is over and we never want to restore a stale "Working…"
 * indicator the next time the operator activates this session.
 */
export type ClearChatWaitStateOpts = {
  /**
   * When true, keep the per-session live snapshot in `chat-live-state.ts`.
   * Tab switches use this so switching back to a working chat can restore
   * the thinking / activity chrome without waiting for the next WS delta.
   */
  preserveLiveSnapshot?: boolean;
};

export function clearChatWaitState(host: ChatWaitFullHost, opts?: ClearChatWaitStateOpts): void {
  stopChatWaitTicker(host);
  host.chatRunId = null;
  if ("chatStream" in host) {
    host.chatStream = null;
  }
  if ("chatReasoningStream" in host) {
    host.chatReasoningStream = null;
  }
  if ("chatStreamStartedAt" in host) {
    host.chatStreamStartedAt = null;
  }
  if ("chatLiveRunHint" in host) {
    host.chatLiveRunHint = null;
  }
  if ("chatActiveFileToolPath" in host) {
    host.chatActiveFileToolPath = null;
  }
  if ("chatMainRun" in host) {
    host.chatMainRun = null;
  }
  // JAISIU-chat-switch-reload — also null the outgoing session's
  // pending flags. `chatSending` is the optimistic flag the chat
  // controller sets at chat.send start and unsets on the matching
  // chat.final; clearing it here prevents a tab-switch mid-run from
  // painting the composer toolbar's pending ring for the *new*
  // session. `chatCollectQueueDepth` is the server-side "queued
  // behind an in-flight run" counter; clearing it here prevents the
  // "Queued (n)" banner from staying pinned to whatever value the
  // outgoing session had. Both are restored on the destination tab
  // by `activateChatSession`'s `consumeLiveState` branch.
  if ("chatSending" in host) {
    (host as { chatSending: boolean }).chatSending = false;
  }
  if ("chatCollectQueueDepth" in host) {
    (host as { chatCollectQueueDepth: number }).chatCollectQueueDepth = 0;
  }
  // Drop the per-session live snapshot on terminal cleanup (run finished,
  // hard reset). Tab switches pass `preserveLiveSnapshot: true` so the
  // outgoing session's thinking state survives until the operator returns.
  if (
    !opts?.preserveLiveSnapshot &&
    typeof (host as { sessionKey?: unknown }).sessionKey === "string"
  ) {
    const sessionKey = (host as { sessionKey: string }).sessionKey;
    if (sessionKey) {
      clearLiveState(host, sessionKey);
    }
  }
}
